import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { FinancialService } from '../../../../core/services/financial/financial.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { BalanceSheetReportModel } from '../../../../core/models/data/balance-sheet-report.model';
import { CurrencyUtilities } from '../../../../core/utilities/currency.utilities';
import { DateUtilities } from '../../../../core/utilities/date.utilities';
import { ReportDateFilterComponent } from '../../components/report-date-filter/report-date-filter.component';
import { GenericMessagingConstants } from '../../../../core/constants/messaging.constants';

interface IdentityChart {
	assetsHeightPct: number;
	liabilitiesHeightPct: number;
	equityHeightPct: number;
	liabilitiesSharePct: number;
	equitySharePct: number;
}

interface KeyRatio {
	label: string;
	value: string;
	trendUp: boolean;
}

@Component({
	selector: 'ms-balance-sheet',
	standalone: true,
	imports: [CommonModule, FaDuotoneIconComponent, ReportDateFilterComponent],
	templateUrl: './balance-sheet.page.html',
	styleUrl: './balance-sheet.page.scss'
})
export class BalanceSheetPage implements OnInit {

	private financialService = inject(FinancialService);
	private userSessionService = inject(UserSessionService);

	public readonly asOfDate = signal<string>(DateUtilities.todayISO());
	public readonly report = signal<BalanceSheetReportModel | undefined>(undefined);
	public readonly isLoading = signal<boolean>(false);
	public readonly errorMessage = signal<string>('');

	public readonly isBalanced = computed<boolean>(() => {
		const current = this.report();
		if (!current) return true;
		return current.assets.total === current.totalLiabilitiesAndEquity;
	});

	public readonly identityChart = computed<IdentityChart | undefined>(() => {
		const current = this.report();
		if (!current) return undefined;

		const assets = Math.max(0, current.assets.total);
		const liabilities = Math.max(0, current.liabilities.total);
		const equity = Math.max(0, current.equity.total);
		const lePlusE = liabilities + equity;
		const max = Math.max(assets, lePlusE);

		if (max === 0) return undefined;

		return {
			assetsHeightPct: (assets / max) * 100,
			liabilitiesHeightPct: (liabilities / max) * 100,
			equityHeightPct: (equity / max) * 100,
			liabilitiesSharePct: lePlusE > 0 ? (liabilities / lePlusE) * 100 : 0,
			equitySharePct: lePlusE > 0 ? (equity / lePlusE) * 100 : 0
		};
	});

	public readonly keyRatios = computed<KeyRatio[]>(() => {
		const current = this.report();
		if (!current) return [];

		const ratios: KeyRatio[] = [];

		if (current.liabilities.total > 0) {
			const coverage = current.assets.total / current.liabilities.total;
			ratios.push({
				label: 'Asset Coverage',
				value: coverage.toFixed(1),
				trendUp: coverage >= 1.5
			});
		}

		if (current.assets.total > 0) {
			const equityRatio = current.equity.total / current.assets.total;
			ratios.push({
				label: 'Equity Ratio',
				value: equityRatio.toFixed(2),
				trendUp: equityRatio >= 0.5
			});
		}

		return ratios;
	});

	public async ngOnInit(): Promise<void> {
		await this.loadReport();
	}

	public async onDateChanged(): Promise<void> {
		if (!this.asOfDate()) return;
		await this.loadReport();
	}

	public formatAmount(amount: number): string {
		const current = this.report();
		if (!current) return '';
		return CurrencyUtilities.convertMinorUnitToDisplayFormat(amount, current.currency);
	}

	private async loadReport(): Promise<void> {
		const organization = this.userSessionService.activeOrganization();
		if (!organization) {
			this.report.set(undefined);
			this.errorMessage.set('An active organization is required to view this report.');
			return;
		}

		this.isLoading.set(true);
		this.errorMessage.set('');

		const result = await this.financialService.getBalanceSheet(organization.organizationId, this.asOfDate());

		if (result.success && result.data) {
			this.report.set(result.data);
		} else {
			this.report.set(undefined);
			this.errorMessage.set(result.message || GenericMessagingConstants.GENERIC_ERROR_OCCURRED());
		}

		this.isLoading.set(false);
	}

}
